import React, { useCallback } from 'react';
import { Avatar } from '@/components/atoms';
import { SearchResultSkeleton } from '@/components/molecules/skeletons';
import { SKELETON } from '@/lib/constants';
import { cn } from '@/lib/utils';
import { useUserNavigation } from '@/hooks/useUserNavigation';

interface User {
  id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  username: string;
  avatar_url: string | null;
}

interface UserSearchResultsProps {
  users: User[];
  isLoading: boolean;
  onSelect?: (userId?: string) => void;
}

export const UserSearchResults: React.FC<UserSearchResultsProps> = ({
  users,
  isLoading,
  onSelect
}) => {
  const { navigateToUser, prefetchUser } = useUserNavigation();

  const handleUserClick = (user: User) => {
    navigateToUser(user.username);
    onSelect?.(user.user_id);
  };

  const handlePrefetch = useCallback((username: string) => {
    prefetchUser(username);
  }, [prefetchUser]);

  if (isLoading) {
    return (
      <div className="space-y-1 p-2">
        {Array.from({ length: SKELETON.SEARCH_RESULTS }, (_, i) => (
          <SearchResultSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="p-4 text-center text-sm text-muted-foreground">
        No users found
      </div>
    );
  }

  return (
    <div className="space-y-1 p-2">
      {users.map((user) => (
        <button
          key={user.user_id}
          onClick={() => handleUserClick(user)}
          onMouseEnter={() => handlePrefetch(user.username)}
          onFocus={() => handlePrefetch(user.username)}
          className={cn(
            "w-full flex items-center gap-2 p-2 rounded-lg",
            "hover:bg-accent transition-colors",
            "focus:outline-none focus:bg-accent",
            "cursor-pointer"
          )}
        >
          <Avatar
            src={user.avatar_url}
            firstName={user.first_name}
            lastName={user.last_name}
            username={user.username}
            alt={`${user.first_name} ${user.last_name}`}
          />
          <div className="flex flex-col items-start flex-1 min-w-0">
            <p className="font-medium text-sm text-foreground truncate w-full text-left">
              {user.first_name} {user.last_name}
            </p>
            <p className="text-xs text-muted-foreground truncate w-full text-left">
              @{user.username}
            </p>
          </div>
        </button>
      ))}
    </div>
  );
};
